import {lazy, Suspense} from 'preact/compat';
import {useCallback, useEffect, useRef} from 'preact/hooks';

import {AuthorizeIframe} from '~/components/Authorize/Iframe';
import type {AuthorizeIframeElement} from '~/components/Authorize/Iframe';
import {Button} from '~/components/Button/Button';
import {ShopIcon} from '~/components/icons/ShopIcon';
import type {ToastElement} from '~/components/Toast/Toast';
import {Toast} from '~/components/Toast/Toast';
import {AuthorizeProvider} from '~/foundation/Authorize/AuthorizeProvider';
import {useI18n} from '~/foundation/I18n/hooks';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import {useEmailListener} from '~/hooks/useEmailListener';
import type {CompletedEvent} from '~/types/event';
import register from '~/utils/register';

import {getAnalyticsProps} from './monorail';
import type {ShopLoginButtonProps} from './types';

const ShopFollowButton = lazy(
  () => import('../ShopFollowButton/ShopFollowButton'),
);

const ShopLoginButtonInternal = ({
  action,
  activatorId,
  email,
  emailInputId,
  hideButton,
  ...props
}: ShopLoginButtonProps) => {
  const {trackPageImpression, trackUserAction} = useMonorail();
  const {translate} = useI18n();
  const activatorElementRef = useRef<HTMLElement | null>(
    activatorId ? document.getElementById(activatorId) : null,
  );
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const iframeRef = useRef<AuthorizeIframeElement | null>(null);
  const toastRef = useRef<ToastElement | null>(null);
  useEmailListener({autoOpen: props.autoOpen, email, emailInputId, iframeRef});

  useEffect(() => {
    if (!hideButton) {
      trackPageImpression({
        page: 'SIGN_IN_WITH_SHOP_BUTTON',
      });
    }
  }, [hideButton, trackPageImpression]);

  const onComplete = useCallback(
    ({email}: CompletedEvent) => {
      let content = translate('shopLoginButton.toast.default');

      if (email) {
        content = translate('shopLoginButton.toast.email', {email});
      }

      toastRef.current?.show(content as string);
    },
    [translate],
  );

  if (action === 'follow') {
    return (
      <Suspense fallback={null}>
        <ShopFollowButton {...props} />
      </Suspense>
    );
  }

  function handleButtonClick() {
    trackUserAction({
      userAction: 'SIGN_IN_WITH_SHOP_BUTTON_CLICK',
    });
  }

  const button =
    hideButton || activatorElementRef.current ? null : (
      <Button onClick={handleButtonClick} ref={buttonRef}>
        <span className="cursor-pointer whitespace-nowrap px-11 py-4 font-sans text-button-large text-white">
          {translate('shopLoginButton.continue', {
            shop: <ShopIcon className="relative inline-block h-4 w-auto" />,
          })}
        </span>
      </Button>
    );

  const activator = activatorElementRef.current
    ? activatorElementRef
    : buttonRef;

  return (
    <>
      {button}
      <AuthorizeIframe
        {...props}
        activator={activator}
        onComplete={onComplete}
        ref={iframeRef}
      />
      <Toast ref={toastRef} />
    </>
  );
};

const getFeatureDictionary = async (locale: string) =>
  // Temporary adding an await here so that the i18n-dynamic-import-replacer
  // replaces the import statement with the correct file.
  // https://github.com/Shopify/shop-identity/issues/2814
  // eslint-disable-next-line no-return-await
  await import(`./translations/${locale}.json`);

register<ShopLoginButtonProps>(
  ({element, ...props}) => (
    <RootProvider
      element={element}
      featureName="ShopLoginButton"
      getFeatureDictionary={getFeatureDictionary}
      monorailProps={getAnalyticsProps(props)}
    >
      <AuthorizeProvider>
        <ShopLoginButtonInternal {...props} />
      </AuthorizeProvider>
    </RootProvider>
  ),
  {
    name: 'shop-login-button',
    props: {
      action: 'string',
      activatorId: 'string',
      analyticsContext: 'string',
      analyticsTraceId: 'string',
      anchorTo: 'string',
      apiKey: 'string',
      autoOpen: 'boolean',
      avoidPayAltDomain: 'boolean',
      checkoutVersion: 'string',
      clientId: 'string',
      codeChallenge: 'string',
      codeChallengeMethod: 'string',
      consentChallenge: 'string',
      disableSignUp: 'boolean',
      email: 'string',
      emailInputId: 'string',
      emailVerificationRequired: 'boolean',
      flow: 'string',
      flowVersion: 'string',
      hideButton: 'boolean',
      keepModalOpen: 'boolean',
      popUpFeatures: 'string',
      popUpName: 'string',
      proxy: 'boolean',
      redirectType: 'string',
      redirectUri: 'string',
      requireVerification: 'boolean',
      responseMode: 'string',
      responseType: 'string',
      scope: 'string',
      state: 'string',
    },
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-login-button']: ShopLoginButtonProps;
    }
  }
}

export function ShopLoginButton(props: ShopLoginButtonProps) {
  return <shop-login-button {...props} />;
}
